import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import vipSeat from 'assets/images/icons/vip_seat.svg'
import backgroundImage from 'assets/images/boat_background.svg'
import { CheckCircleIcon, LockClosedIcon, TicketIcon } from '@heroicons/react/24/outline'

/**
 * VIP-Only Seat Blocking Layout Component
 * Handles the custom VIP-only vessel layout with 32 VIP seats for seat blocking/unblocking
 * Supports multi-select for blocking or releasing seats
 */
export function VIPSeatBlockingLayout({
    seatLayout,
    selectedSeats,
    onSeatToggle,
    availableSeats,
}) {
    const { t } = useTranslation()

    // Get seat status from available seats data
    const getSeatStatus = (seatNo) => {
        const seat = availableSeats.find(s => s.seat_no === seatNo)
        return seat?.seat_status || null
    }

    // Check if seat is available for selection based on current selection type
    const isSeatSelectable = (seatNo, seatStatus) => {
        if (seatStatus === 'BKD' || seatStatus === 'BOOKED') return false
        if (selectedSeats.length === 0) return true
        
        // Get the status of the first selected seat
        const firstSelectedSeatStatus = getSeatStatus(selectedSeats[0])
        
        // Normalize statuses for comparison (treat null/AVL as same, BLK as BLK)
        const normalizeStatus = (s) => s === 'BLK' ? 'BLK' : 'AVL'
        
        // Only allow selecting seats of the same status type
        return normalizeStatus(seatStatus) === normalizeStatus(firstSelectedSeatStatus)
    }

    /**
     * Render individual VIP seat
     * @param {string} seatNo - Seat number in format "A1", "B2", etc.
     */
    const renderVIPSeat = (seatNo) => {
        const seat = seatLayout.VIP.find(s => s.seat_no === seatNo)
        if (!seat) return <div className="w-[30px] h-[30px] bg-[#e0e8e9] rounded-md"></div>
        
        const seatStatus = getSeatStatus(seatNo)
        const isSelected = selectedSeats.includes(seatNo)
        const isBooked = seatStatus === 'BKD' || seatStatus === 'BOOKED'
        const isBlocked = seatStatus === 'BLK'
        // Check availability from the seat object or derive from status
        const isAvailable = seat.available !== false && !isBooked
        const canSelect = (isAvailable || isBlocked) && isSeatSelectable(seatNo, seatStatus)

        // Determine seat styling - matching booking screen style
        let seatClasses = 'relative flex items-center justify-center rounded-md cursor-pointer h-[30px] w-[30px] border-2 transition-all duration-200 '
        
        if ((!isAvailable && !isBlocked) || isBooked) {
            seatClasses += 'bg-[#e0e8e9] border-[0px] text-gray-400 cursor-not-allowed'
        } else if (isSelected) {
            seatClasses += 'bg-[#128472] border-[#128472] text-white shadow-lg'
        } else if (!canSelect && selectedSeats.length > 0) {
            seatClasses += 'bg-[#e0e8e9] border-[0px] text-gray-400 cursor-not-allowed opacity-50'
        } else if (isBlocked) {
            seatClasses += 'bg-orange-100 border-[1px] border-orange-400 text-orange-700 shadow-sm hover:shadow-md'
        } else {
            seatClasses += 'bg-white border-[1px] border-[#e3bf02] text-[#9b7d00] shadow-sm hover:shadow-md'
        }

        return (
            <div
                className={seatClasses}
                onClick={() => {
                    if (canSelect) {
                        onSeatToggle(seatNo, seatStatus, isAvailable)
                    }
                }}
            >
                <span className="text-[10px] font-bold">{seatNo}</span>
                
                {/* Status badge */}
                {isBooked && (
                    <LockClosedIcon className="absolute -top-1 -right-1 h-3 w-3 text-red-600 bg-white rounded-full p-0.5" />
                )}
                {isBlocked && !isSelected && (
                    <TicketIcon className="absolute -top-1 -right-1 h-3 w-3 text-orange-600 bg-white rounded-full p-0.5" />
                )}
                {isSelected && (
                    <CheckCircleIcon className="absolute -top-1 -right-1 h-3.5 w-3.5 text-white bg-[#128472] rounded-full" />
                )}
            </div>
        )
    }

    return (
        <div className="w-full relative h-auto">
            <div
                className="relative border-solid border-[#e0e8e9]"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundSize: '95% auto',
                    minHeight: '700px',
                }}
            >
                {/* VIP Seats Label */}
                <div className="flex w-full justify-center text-center text-[14px] font-semibold text-primary pt-[14rem] pb-2">
                    <img src={vipSeat} className="w-[20px] me-2" alt="VIP" />
                    {t('VIP Seats')}
                </div>
                
                {/* Main Layout: Left and Right Sections */}
                <div className="flex justify-center gap-20 px-6">
                    {/* Left Section: A and C/E */}
                    <div className="flex-1 max-w-[180px]">
                        {/* A Section */}
                        <div className="mb-2">
                            <div className="text-center font-bold text-primary mb-3">A</div>
                            {/* Row 1: A2, A1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A2')}
                                {renderVIPSeat('A1')}
                            </div>
                            {/* Row 2: A3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 3: A4 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('A4')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 4: A5, A6 */}
                            <div className="flex justify-start gap-3">
                                {renderVIPSeat('A5')}
                                {renderVIPSeat('A6')}
                            </div>
                        </div>
                        
                        {/* C/E Section */}
                        <div>
                            <div className="text-center font-bold text-primary mb-2">C/E</div>
                            {/* Row 1: C2, C1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C2')}
                                {renderVIPSeat('C1')}
                            </div>
                            {/* Row 2: C3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 3: C4, C5 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('C4')}
                                {renderVIPSeat('C5')}
                            </div>
                            {/* Row 4: E2, E1 */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('E2')}
                                {renderVIPSeat('E1')}
                            </div>
                            {/* Row 5: E3 (left) */}
                            <div className="flex justify-start gap-3 mb-2">
                                {renderVIPSeat('E3')}
                                <div className="w-[30px]"></div>
                            </div>
                            {/* Row 6: E4, E5 */}
                            <div className="flex justify-start gap-3">
                                {renderVIPSeat('E4')}
                                {renderVIPSeat('E5')}
                            </div>
                        </div>
                    </div>
                    
                    {/* Right Section: B and D/F */}
                    <div className="flex-1 max-w-[180px]">
                        {/* B Section */}
                        <div className="mb-2">
                            <div className="text-center font-bold text-primary mb-3">B</div>
                            {/* Row 1: B1, B2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('B1')}
                                {renderVIPSeat('B2')}
                            </div>
                            {/* Row 2: B3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('B3')}
                            </div>
                            {/* Row 3: B4 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                <div className="w-[30px]"></div>
                                {renderVIPSeat('B4')}
                            </div>
                            {/* Row 4: B6, B5 */}
                            <div className="flex justify-end gap-3">
                                {renderVIPSeat('B6')}
                                {renderVIPSeat('B5')}
                            </div>
                        </div>
                        
                        {/* D/F Section */}
                        <div>
                            <div className="text-center font-bold text-primary mb-2">D/F</div>
                            {/* Row 1: D1, D2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D1')}
                                {renderVIPSeat('D2')}
                            </div>
                            {/* Row 2: D3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D3')}
                            </div>
                            {/* Row 3: D5, D4 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('D5')}
                                {renderVIPSeat('D4')}
                            </div>
                            {/* Row 4: F1, F2 */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('F1')}
                                {renderVIPSeat('F2')}
                            </div>
                            {/* Row 5: F3 (right) */}
                            <div className="flex justify-end gap-3 mb-2">
                                {renderVIPSeat('F3')}
                            </div>
                            {/* Row 6: F5, F4 */}
                            <div className="flex justify-end gap-3">
                                {renderVIPSeat('F5')}
                                {renderVIPSeat('F4')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

VIPSeatBlockingLayout.propTypes = {
    seatLayout: PropTypes.object.isRequired,
    selectedSeats: PropTypes.array.isRequired,
    onSeatToggle: PropTypes.func.isRequired,
    availableSeats: PropTypes.array.isRequired,
}
